import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useBusiness } from "../contexts/BusinessContext";
import {
  Users, Truck, Plus, Edit2, Trash2, Save, X, DollarSign,
  Settings, MapPin, Phone, FileText
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function RentalConfigTab() {
  const { businessId } = useBusiness();
  const [activeSection, setActiveSection] = useState("operators");
  
  // Operators state
  const [operators, setOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(true);
  const [editingOperator, setEditingOperator] = useState(null);
  const [newOperator, setNewOperator] = useState({
    name: "",
    equipment_types: [],
    daily_cost: 0,
    phone: "",
    notes: ""
  });
  const [showOperatorForm, setShowOperatorForm] = useState(false);
  
  // Freight zones state
  const [freightZones, setFreightZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(true);
  const [editingZone, setEditingZone] = useState(null);
  const [newZone, setNewZone] = useState({
    name: "",
    min_distance_km: 0,
    max_distance_km: 0,
    round_trip_cost: 0,
    notes: "",
    order: 0
  });
  const [showZoneForm, setShowZoneForm] = useState(false);
  
  // Equipment types for operators
  const equipmentTypes = [
    "Excavadora 320",
    "Excavadora 336",
    "Retroexcavadora",
    "Cargador Frontal",
    "Bulldozer",
    "Motoconformadora",
    "Rodillo",
    "Camión Volteo",
    "Grúa"
  ];
  
  // Load data
  useEffect(() => {
    if (businessId) {
      fetchOperators();
      fetchFreightZones();
    }
  }, [businessId]);
  
  const fetchOperators = async () => {
    try {
      setLoadingOperators(true);
      const response = await axios.get(`${API}/api/operators?business_id=${businessId}`);
      if (response.data.success) {
        setOperators(response.data.operators);
      }
    } catch (error) {
      toast.error("Error al cargar operadores");
    } finally {
      setLoadingOperators(false);
    }
  };
  
  const fetchFreightZones = async () => {
    try {
      setLoadingZones(true);
      const response = await axios.get(`${API}/api/freight-zones?business_id=${businessId}`);
      if (response.data.success) {
        setFreightZones(response.data.zones);
      }
    } catch (error) {
      toast.error("Error al cargar zonas de flete");
    } finally {
      setLoadingZones(false);
    }
  };
  
  // Operator CRUD
  const handleSaveOperator = async () => {
    if (!newOperator.name) {
      toast.error("El nombre es requerido");
      return;
    }
    
    try {
      if (editingOperator) {
        const response = await axios.put(`${API}/api/operators/${editingOperator.id}`, newOperator);
        if (response.data.success) {
          toast.success("Operador actualizado");
          fetchOperators();
        }
      } else {
        const response = await axios.post(`${API}/api/operators`, {
          ...newOperator,
          business_id: businessId
        });
        if (response.data.success) {
          toast.success("Operador creado");
          fetchOperators();
        }
      }
      
      setShowOperatorForm(false);
      setEditingOperator(null);
      setNewOperator({ name: "", equipment_types: [], daily_cost: 0, phone: "", notes: "" });
    } catch (error) {
      toast.error("Error al guardar operador");
    }
  };
  
  const handleEditOperator = (operator) => {
    setEditingOperator(operator);
    setNewOperator({
      name: operator.name,
      equipment_types: operator.equipment_types || [],
      daily_cost: operator.daily_cost || 0,
      phone: operator.phone || "",
      notes: operator.notes || ""
    });
    setShowOperatorForm(true);
  };
  
  const handleDeleteOperator = async (operatorId) => {
    if (!window.confirm("¿Eliminar este operador?")) return;
    
    try {
      await axios.delete(`${API}/api/operators/${operatorId}`);
      toast.success("Operador eliminado");
      fetchOperators();
    } catch (error) {
      toast.error("Error al eliminar operador");
    }
  };
  
  // Freight zone CRUD
  const handleSaveZone = async () => {
    if (!newZone.name) {
      toast.error("El nombre es requerido");
      return;
    }
    
    try {
      if (editingZone) {
        const response = await axios.put(`${API}/api/freight-zones/${editingZone.id}`, newZone);
        if (response.data.success) {
          toast.success("Zona actualizada");
          fetchFreightZones();
        }
      } else {
        const response = await axios.post(`${API}/api/freight-zones`, {
          ...newZone,
          business_id: businessId
        });
        if (response.data.success) {
          toast.success("Zona creada");
          fetchFreightZones();
        }
      }
      
      setShowZoneForm(false);
      setEditingZone(null);
      setNewZone({ name: "", min_distance_km: 0, max_distance_km: 0, round_trip_cost: 0, notes: "", order: 0 });
    } catch (error) {
      toast.error("Error al guardar zona");
    }
  };
  
  const handleEditZone = (zone) => {
    setEditingZone(zone);
    setNewZone({
      name: zone.name,
      min_distance_km: zone.min_distance_km || 0,
      max_distance_km: zone.max_distance_km || 0,
      round_trip_cost: zone.round_trip_cost || 0,
      notes: zone.notes || "",
      order: zone.order || 0
    });
    setShowZoneForm(true);
  };
  
  const handleDeleteZone = async (zoneId) => {
    if (!window.confirm("¿Eliminar esta zona de flete?")) return;
    
    try {
      await axios.delete(`${API}/api/freight-zones/${zoneId}`);
      toast.success("Zona eliminada");
      fetchFreightZones();
    } catch (error) {
      toast.error("Error al eliminar zona");
    }
  };
  
  const toggleEquipmentType = (type) => {
    setNewOperator(prev => ({
      ...prev,
      equipment_types: prev.equipment_types.includes(type)
        ? prev.equipment_types.filter(t => t !== type)
        : [...prev.equipment_types, type]
    }));
  };

  return (
    <div className="space-y-6" data-testid="rental-config-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Settings className="w-7 h-7 text-orange-500" />
            Configuración de Renta
          </h2>
          <p className="text-slate-400 mt-1">Administra operadores y zonas de flete para cotizaciones de renta</p>
        </div>
      </div>
      
      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <button
          onClick={() => setActiveSection("operators")}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 ${
            activeSection === "operators"
              ? "bg-blue-600 text-white"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700"
          }`}
        >
          <Users className="w-4 h-4" />
          Operadores
        </button>
        <button
          onClick={() => setActiveSection("freight")}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 ${
            activeSection === "freight"
              ? "bg-blue-600 text-white"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700"
          }`}
        >
          <Truck className="w-4 h-4" />
          Zonas de Flete
        </button>
      </div>
      
      {/* Operators Section */}
      {activeSection === "operators" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Operadores de Maquinaria</h3>
            <button
              onClick={() => {
                setEditingOperator(null);
                setNewOperator({ name: "", equipment_types: [], daily_cost: 0, phone: "", notes: "" });
                setShowOperatorForm(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Nuevo Operador
            </button>
          </div>
          
          {/* Operator Form */}
          {showOperatorForm && (
            <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
              <h4 className="text-lg font-semibold text-white mb-4">
                {editingOperator ? "Editar Operador" : "Nuevo Operador"}
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Nombre / Tipo *</label>
                  <input
                    type="text"
                    value={newOperator.name}
                    onChange={(e) => setNewOperator({ ...newOperator, name: e.target.value })}
                    placeholder="Ej: Operador Maquinaria 320"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Costo por Día (MXN)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="number"
                      value={newOperator.daily_cost}
                      onChange={(e) => setNewOperator({ ...newOperator, daily_cost: parseFloat(e.target.value) || 0 })}
                      className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Teléfono</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      value={newOperator.phone}
                      onChange={(e) => setNewOperator({ ...newOperator, phone: e.target.value })}
                      className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Notas</label>
                  <input
                    type="text"
                    value={newOperator.notes}
                    onChange={(e) => setNewOperator({ ...newOperator, notes: e.target.value })}
                    placeholder="Certificaciones, experiencia, etc."
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm text-slate-400 mb-2">Tipos de Equipo que Opera</label>
                <div className="flex flex-wrap gap-2">
                  {equipmentTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => toggleEquipmentType(type)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        newOperator.equipment_types.includes(type)
                          ? "bg-blue-600 text-white"
                          : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowOperatorForm(false);
                    setEditingOperator(null);
                  }}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                >
                  <X className="w-4 h-4 inline mr-1" />
                  Cancelar
                </button>
                <button
                  onClick={handleSaveOperator}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Save className="w-4 h-4 inline mr-1" />
                  Guardar
                </button>
              </div>
            </div>
          )}
          
          {/* Operators List */}
          {loadingOperators ? (
            <div className="text-center py-8 text-slate-400">Cargando operadores...</div>
          ) : operators.length === 0 ? (
            <div className="text-center py-8 bg-slate-800 rounded-lg">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No hay operadores configurados</p>
              <p className="text-slate-500 text-sm">Agrega operadores para poder incluirlos en cotizaciones de renta</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {operators.map(operator => (
                <div
                  key={operator.id}
                  className="bg-slate-800 rounded-lg p-4 border border-slate-700 flex items-center justify-between"
                >
                  <div>
                    <h4 className="text-white font-medium">{operator.name}</h4>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        ${operator.daily_cost?.toLocaleString()}/día
                      </span>
                      {operator.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {operator.phone}
                        </span>
                      )}
                    </div>
                    {operator.equipment_types?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {operator.equipment_types.map(type => (
                          <span key={type} className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded">
                            {type}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditOperator(operator)}
                      className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteOperator(operator.id)}
                      className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Freight Zones Section */}
      {activeSection === "freight" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Zonas de Flete</h3>
            <button
              onClick={() => {
                setEditingZone(null);
                setNewZone({ name: "", min_distance_km: 0, max_distance_km: 0, round_trip_cost: 0, notes: "", order: freightZones.length });
                setShowZoneForm(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Nueva Zona
            </button>
          </div>
          
          {/* Zone Form */}
          {showZoneForm && (
            <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
              <h4 className="text-lg font-semibold text-white mb-4">
                {editingZone ? "Editar Zona de Flete" : "Nueva Zona de Flete"}
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Nombre de la Zona *</label>
                  <input
                    type="text"
                    value={newZone.name}
                    onChange={(e) => setNewZone({ ...newZone, name: e.target.value })}
                    placeholder="Ej: Zona Metropolitana"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Distancia Mínima (km)</label>
                  <input
                    type="number"
                    value={newZone.min_distance_km}
                    onChange={(e) => setNewZone({ ...newZone, min_distance_km: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Distancia Máxima (km)</label>
                  <input
                    type="number"
                    value={newZone.max_distance_km}
                    onChange={(e) => setNewZone({ ...newZone, max_distance_km: parseInt(e.target.value) || 0 })}
                    placeholder="0 = sin límite"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Costo Ida y Vuelta (MXN)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="number"
                      value={newZone.round_trip_cost}
                      onChange={(e) => setNewZone({ ...newZone, round_trip_cost: parseFloat(e.target.value) || 0 })}
                      className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Orden</label>
                  <input
                    type="number"
                    value={newZone.order}
                    onChange={(e) => setNewZone({ ...newZone, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Notas</label>
                  <input
                    type="text"
                    value={newZone.notes}
                    onChange={(e) => setNewZone({ ...newZone, notes: e.target.value })}
                    placeholder="Información adicional"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowZoneForm(false);
                    setEditingZone(null);
                  }}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                >
                  <X className="w-4 h-4 inline mr-1" />
                  Cancelar
                </button>
                <button
                  onClick={handleSaveZone}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Save className="w-4 h-4 inline mr-1" />
                  Guardar
                </button>
              </div>
            </div>
          )}
          
          {/* Zones List */}
          {loadingZones ? (
            <div className="text-center py-8 text-slate-400">Cargando zonas de flete...</div>
          ) : freightZones.length === 0 ? (
            <div className="text-center py-8 bg-slate-800 rounded-lg">
              <Truck className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No hay zonas de flete configuradas</p>
              <p className="text-slate-500 text-sm">Configura zonas para agregar costos de flete a las cotizaciones</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {freightZones.map(zone => (
                <div
                  key={zone.id}
                  className="bg-slate-800 rounded-lg p-4 border border-slate-700 flex items-center justify-between"
                >
                  <div>
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-orange-500" />
                      {zone.name}
                    </h4>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                      <span>
                        {zone.min_distance_km} - {zone.max_distance_km || "∞"} km
                      </span>
                      <span className="flex items-center gap-1 text-green-400">
                        <DollarSign className="w-3 h-3" />
                        ${zone.round_trip_cost?.toLocaleString()} (ida y vuelta)
                      </span>
                    </div>
                    {zone.notes && (
                      <p className="text-xs text-slate-500 mt-1">{zone.notes}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditZone(zone)}
                      className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteZone(zone.id)}
                      className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Suggested Zones */}
          {freightZones.length === 0 && (
            <div className="bg-slate-800/50 rounded-lg p-4 border border-dashed border-slate-600">
              <h4 className="text-sm font-medium text-slate-300 mb-2">Zonas Sugeridas</h4>
              <p className="text-xs text-slate-500 mb-3">Haz clic en una zona para agregarla rápidamente:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Zona Metropolitana", min: 0, max: 50 },
                  { name: "Hasta 150 km", min: 51, max: 150 },
                  { name: "Hasta 300 km", min: 151, max: 300 },
                  { name: "Hasta 500 km", min: 301, max: 500 },
                  { name: "Más de 500 km", min: 501, max: 0 }
                ].map(suggestion => (
                  <button
                    key={suggestion.name}
                    onClick={() => {
                      setNewZone({
                        name: suggestion.name,
                        min_distance_km: suggestion.min,
                        max_distance_km: suggestion.max,
                        round_trip_cost: 0,
                        notes: "",
                        order: freightZones.length
                      });
                      setShowZoneForm(true);
                    }}
                    className="px-3 py-1 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600 transition-colors"
                  >
                    + {suggestion.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RentalConfigTab;
